// frontend/src/pages/Currency/EditCurrency.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DollarSign } from "lucide-react";
import "./Currency.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EditCurrency = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState({ name: "", symbol: "", code: "" });
  const [currency, setCurrency] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const res = await axios.get(`${url}/api/currency/${id}`, {
          headers: getAuthHeaders(),
        });

        const cur = res.data.data;
        setCurrency(cur);
        setData({
          name: cur.name || "",
          symbol: cur.symbol || "",
          code: cur.code || "",
        });
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load currency");
        navigate("/ViewCurrency");
      } finally {
        setLoading(false);
      }
    };
    fetchCurrency();
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!data.name.trim()) {
      toast.error("Currency name is required");
      return;
    }
    if (!data.symbol.trim()) {
      toast.error("Currency symbol is required");
      return;
    }
    if (!data.code.trim()) {
      toast.error("Currency code is required");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${url}/api/currency/${id}`, data, {
        headers: getAuthHeaders(),
      });

      toast.success("Currency updated successfully");
      navigate("/ViewCurrency");
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading currency details...</div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="currency-form-container">
        <h2 className="form-title">
          <DollarSign size={20} /> Edit Currency
        </h2>

        {currency && (
          <div className="entity-info">
            <div className="info-item">
              <span>Created by: {currency.created_by?.name} on {new Date(currency.created_at).toLocaleDateString()}</span>
            </div>
            {currency.updated_by && (
              <div className="info-item">
                <span>Last updated by: {currency.updated_by?.name} on {new Date(currency.updated_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        )}

        <form className="currency-form" onSubmit={handleSubmit}>
          <div className="form-field filled">
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              placeholder="Currency Name"
              required
            />
            <label>Currency Name</label>
          </div>

          <div className="form-field filled">
            <input
              type="text"
              value={data.symbol}
              onChange={(e) => setData({ ...data, symbol: e.target.value })}
              placeholder="Symbol (e.g., $, Ksh)"
              required
            />
            <label>Symbol</label>
          </div>

          <div className="form-field filled">
            <input
              type="text"
              value={data.code}
              onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })}
              placeholder="ISO Code (e.g., USD, KES)"
              required
            />
            <label>Code</label>
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate("/ViewCurrency")}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Updating..." : "Update Currency"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCurrency;